<template lang="html">
  <section class="screen-overview">
    <header class="green-header" :class="{red: currentWalletBalance < 0}">
      <prt-expense-summary :amount="currentWalletBalance" id="cwb" cwb title="Current Wallet Balance" />
    </header>
    <transition-group class="icon-charts" name="fade" tag="section">
      <prt-icon-chart :icon="iconChart.icon" :key="iconChart.icon" :value="iconChart.value" v-for="iconChart in iconCharts"/>
    </transition-group>
    <section class="expense-summaries">
      <prt-expense-summary :amount="amountSpentThisWeek" title="Spent this week"/>
      <prt-expense-summary :amount="amountSpentThisMonth" title="Spent this month"/>
    </section>
    <transition-group class="recent-transactions" name="grow" tag="section" v-if="recentTransactions.length > 0">
      <h1 key="heading">Recent Transactions</h1>
      <prt-transaction :amount="transaction.amount" :description="transaction.description" :icon="transaction.icon" :key="transaction.id" :timestamp="transaction.createdAt" v-for="transaction in recentTransactions" @delete="deleteTransaction(transaction.id)"/>
    </transition-group>
    <div class="recent-transactions-empty" v-else>
      <h1>No recent transactions.</h1>
    </div>
  </section>
</template>

<script>
import { startOfMonth, startOfWeek } from 'date-fns';

import PrtExpenseSummary from './ui/PrtExpenseSummary.vue';
import PrtIconChart from './ui/PrtIconChart.vue';
import PrtTransaction from './ui/PrtTransaction.vue';

export default {
  components: {
    PrtExpenseSummary,
    PrtIconChart,
    PrtTransaction,
  },
  computed: {
    amountSpentThisWeek() {
      const weekTransactions =
        this.$store.state.application.transactions.filter(transaction =>
          transaction.createdAt > startOfWeek(Date.now(), { weekStartsOn: 1 }).valueOf() && transaction.amount < 0);
      let total = 0;

      weekTransactions.forEach((transaction) => {
        total += transaction.amount;
      });

      return total;
    },
    amountSpentThisMonth() {
      const monthTransactions =
        this.$store.state.application.transactions.filter(transaction =>
          transaction.createdAt > startOfMonth(Date.now()).valueOf() && transaction.amount < 0);
      let total = 0;

      monthTransactions.forEach((transaction) => {
        total += transaction.amount;
      });

      return total;
    },
    currentWalletBalance() {
      return this.$store.state.user.currentWalletBalance;
    },
    iconCharts() {
      // get all negative transactions of this month
      const negativeTransactions =
        this.$store.state.application.transactions.filter(transaction => transaction.amount < 0 &&
            transaction.createdAt > startOfMonth(Date.now()).valueOf());

      const icons = {};
      const iconCharts = [];
      let total = 0;

      // aggregate amount per icon
      negativeTransactions.forEach((transaction) => {
        if (icons[transaction.icon]) icons[transaction.icon] += transaction.amount;
        else icons[transaction.icon] = transaction.amount;
        total += transaction.amount;
      });

      // sort to get the highest three
      const order = Object.keys(icons).sort((a, b) => icons[a] - icons[b]).slice(0, 3);

      // add an icon chart for each icon in the order
      order.forEach((icon) => {
        const chart = {};
        chart.icon = icon;
        chart.value = icons[icon] / total;
        iconCharts.push(chart);
      });

      return iconCharts;
    },
    recentTransactions() {
      return this.$store.state.application.transactions.slice(0, 7);
    },
  },
  created() {
    this.$store.dispatch('initializeRecentTransactions').catch(err => this.$store.commit('addToast', { content: `Could not load recent transactions: ${err.message}`, color: 'negative', timeout: 5000 }));
  },
  data() {
    return {
      fabVisible: true,
      showAddExpense: false,
    };
  },
  methods: {
    deleteTransaction(id) {
      this.$store.dispatch('deleteTransaction', id).catch(err => this.$store.commit('addToast', { content: `Could not delete transaction: ${err.message}`, color: 'negative', timeout: 5000 }));
    },
  },
  name: 'Overview',
};
</script>

<style lang="stylus" scoped>
  @require '../styles/colors'

  .screen-overview
    height: 100vh
    overflow-x: hidden
    overflow-y: auto

    .green-header
      background-color: positive
      color: white
      height: 179px
      display: flex
      justify-content: center
      align-items: center
      transition: background-color 200ms ease

      &.red
        background-color: negative

    .icon-charts
      text-align: center
      max-width: 1200px
      margin: 0 auto

      &:empty
        margin-bottom: 32px

      .fade-enter-active,
      .fade-leave-active,
      .fade-move
        transition: transform 200ms ease, opacity 200ms ease

      .fade-leave-active
        position: absolute

      .fade-enter
        transform: translateY(24px)
        opacity: 0

      .fade-leave-to
        transform: translateY(-24px)
        opacity: 0

    .expense-summaries
      display: flex
      justify-content: space-around
      padding: 0 16px
      margin: 0 auto 32px auto
      max-width: 1200px

    .recent-transactions
      margin: 0 auto 62px auto
      max-width: 1200px

      h1
        margin-left: 12px
        font-size: 10px
        font-weight: bold
        letter-spacing: 1px
        text-transform: uppercase
        color: black-54

      .grow-enter-active,
      .grow-leave-active
        transition: height 200ms ease, transform 200ms ease

      .grow-move
        transition: transform 200ms ease

      .grow-enter,
      .grow-leave-to
        height: 0

      .grow-leave-to
        transform: translateX(100vw);

    .recent-transactions-empty
      min-height: 40%
      display: flex
      align-items: center
      justify-content: center

      h1
        color: black-38
        text-align: center
        font-size: 1rem

</style>
